// src/modules/estimateDetails/EstimateDetailForm.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Input from '../../components/shared/Input';
import Button from '../../components/shared/Button';
import { createEstimateDetail, updateEstimateDetail, getEstimateDetailsById } from './EstimateDetailService'; // Corrected function import
import DashboardLayout from '../../layouts/DashboardLayout';
import { useNavigate, useParams } from 'react-router-dom'; // Corrected to useParams to get the ID from URL
import LoadingSpinner from '../../components/shared/LoadingSpinner'; // Use the LoadingSpinner component
import {
  FormContainer,
  FormSection,
  FormHeader,
  BackButton,
  FormGrid,
  FormField,
  SubmitButton,
} from '../../components/styled/FormComponents';
import { FaArrowLeft, FaClipboardList, FaSave, FaSpinner } from 'react-icons/fa';

// Styled components
const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing(3)};
  padding: ${(props) => props.theme.spacing(4)};
  background-color: ${(props) => props.theme.colors.surface};
  border-radius: ${(props) => props.theme.borderRadius};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
`;

const SummaryTitle = styled.h3`
  font-size: 1.5em;
  margin: auto;
  color: ${(props) => props.theme.colors.primaryText};
  margin-bottom: ${(props) => props.theme.spacing(3)};
`;

const Message = styled.div`
  color: ${(props) => (props.isError ? props.theme.colors.error : props.theme.colors.success)};
  background-color: ${(props) => (props.isError ? '#fdecea' : '#e6f7f1')};
  padding: ${(props) => props.theme.spacing(2)};
  border-radius: ${(props) => props.theme.borderRadius};
  margin-bottom: ${(props) => props.theme.spacing(2)};
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${(props) => props.theme.spacing(3)};
`;

// Main component
const EstimateDetailForm = ({ onSave = () => {}, initialData = {} }) => {
  const [name, setName] = useState(initialData.name || '');
  const [description, setDescription] = useState(initialData.description || '');
  const [message, setMessage] = useState(null); // Holds success or error message
  const [isError, setIsError] = useState(false); // Flag to indicate if message is an error
  const [isEdit, setIsEdit] = useState(false); // State to track if the form is in edit mode
  const navigate = useNavigate(); // Replace useHistory with useNavigate
  const { id } = useParams(); // Get ID from URL
  const [loading, setLoading] = useState(id ? true : false);
  const [submitting, setSubmitting] = useState(false);
  

  // Fetch detail data if it's an edit mode
  useEffect(() => {
    const fetchData = async () => {
      if (id) { // Check if the id is present, indicating an edit mode
        setLoading(true);
        try {
          setIsEdit(true); // Set edit mode based on presence of id
          const response = await getEstimateDetailsById(id); // Fetch estimate detail data by ID
          setName(response.name || '');
          setDescription(response.description || '');
          setLoading(false); // Set loading to false after data is fetched
        } catch (error) {
          setMessage(`Error loading estimate detail: ${error.message}`);
          setIsError(true);
          setLoading(false);
        }finally{
          setLoading(false);
        }
      } else {
        setIsEdit(false); // Set edit mode to false if no id is found
        setLoading(false); // No need to load if not in edit mode
      }
    };

    fetchData();
  }, [id]); // Only run on component mount or when id changes

  const handleSave = async () => {
    // Validate name and description before saving
    if (!name) {
      setMessage('Please fill out all required fields.');
      setIsError(true);
      return;
    }

    const detailData = { name, description };

    try {
      if (isEdit) {
        await updateEstimateDetail(id, detailData); // Use ID from URL
        setMessage('Estimate detail updated successfully!');
        setIsError(false);
      } else {
        await createEstimateDetail(detailData);
        setMessage('Estimate detail created successfully!');
        setIsError(false);
      }
      onSave(); // Ensure onSave is called, if provided
      navigate('/estimates/detail'); // Redirect to estimate detail list after saving
    } catch (error) {
      setMessage(`Failed to save estimate detail: ${error.message}`);
      setIsError(true);
    }
  };

  return (
    <DashboardLayout>
      {loading ? (
        <LoadingSpinner /> // Use the LoadingSpinner component while data is being fetched
      ) : (

        <>

        <FormContainer>
          <FormHeader>
          <BackButton onClick={() => navigate('/estimates/detail')}>
            <FaArrowLeft /> Back
          </BackButton>
          <h2>{id ? 'Edit Estimate Extra Details' : 'New Estimate Extra Details'}</h2>
        </FormHeader>

        <form onSubmit={handleSave}>
        <FormSection>
        {message && <Message isError={isError}>{message}</Message>}
          <FormGrid>
          <FormField index={0}>
                <Input
                  label="Detail Name"
                  icon={<FaClipboardList />}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter detail name"
                  required
                />
              </FormField>
          </FormGrid>
          <FormGrid>
          <FormField index={1}>
          <Input
            label="Description"
            icon={<FaClipboardList />}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter description"
          />
              </FormField>
          </FormGrid>
        </FormSection>

        <SubmitButton type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <FaSpinner className="icon-spin" />
                {id ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <FaSave />
                {id ? 'Update Detail' : 'Create Detail'}
              </>
            )}
          </SubmitButton>
        </form>

        </FormContainer>

        </>
      )}
    </DashboardLayout>
  );
};

export default EstimateDetailForm;
